import { useRef, type PointerEvent as ReactPointerEvent } from "react";

/**
 * The horizontal drag that reaches Ask, and the one that comes back from it.
 *
 * Ask is the pane immediately right of a situation, a company or a filing, a neighbour
 * rather than a destination, so the page slides left and Ask comes in.
 *
 * ## It must lose to scrolling, and it does so by default
 *
 * A reading page scrolls vertically, and this gesture must never take a press meant for
 * that. The direction test refuses anything not decisively horizontal, and `pointercancel`,
 * which the browser fires when it takes a touch over for scrolling, abandons the gesture.
 * Nothing here calls `preventDefault`, so the browser wins every contested drag.
 *
 * ## And none of that works without `touch-action: pan-y pinch-zoom` on `.app`
 *
 * Under the default `auto` the compositor claims every touch on a scrollable page as soon
 * as it moves, so `pointercancel` fires on every drag, `pointerup` never arrives, and this
 * hook never fires. The declaration in `mosaic.css` makes the gesture reachable, and the
 * unit tests here cannot see its absence. Read the comment there before changing either.
 *
 * ## Touch only
 *
 * A mouse drag across a page is a text selection, and claiming it would make selecting a
 * quote open a panel. `pointerType` says which is which.
 */

/**
 * How far a drag has to travel before it means anything, in CSS pixels: past a tap's
 * jitter and well under a screen's width.
 */
export const SWIPE_MIN_TRAVEL = 48;

/**
 * How much more horizontal than vertical the drag has to be. A diagonal is ambiguous and
 * resolves to the scroll.
 */
export const SWIPE_INTENT_RATIO = 1.6;

/** The way a recognised swipe travelled across the screen. */
export type SwipeDirection = "left" | "right";

/**
 * The whole judgement, as one pure function: did this drag mean to go somewhere, and
 * which way. Evaluated on release rather than mid-drag, because a gesture the browser
 * decided to scroll with never reaches release at all.
 */
export function swipeDirection(dx: number, dy: number): SwipeDirection | null {
  if (Math.abs(dx) < SWIPE_MIN_TRAVEL) return null;
  if (Math.abs(dx) < Math.abs(dy) * SWIPE_INTENT_RATIO) return null;
  return dx < 0 ? "left" : "right";
}

/**
 * Where a drag may not start.
 *
 * A table of insider transactions and the dense radar table both scroll sideways inside
 * their own box, and a drag there is the reader moving that box — taking it would move
 * the whole page instead of the columns they were reading. Text fields are the same
 * argument with a caret instead of a scrollbar.
 */
export function swipeAllowedFrom(target: EventTarget | null): boolean {
  let node = target instanceof Element ? target : null;
  while (node && node !== document.body) {
    if (node.matches("input, textarea, select, [contenteditable]")) return false;
    if (node.scrollWidth > node.clientWidth) {
      const overflow = getComputedStyle(node).overflowX;
      if (overflow === "auto" || overflow === "scroll") return false;
    }
    node = node.parentElement;
  }
  return true;
}

/** Pointer handlers returned by `useEdgeSwipe`, to spread onto one element. */
export interface EdgeSwipeHandlers {
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: () => void;
}

/**
 * Spread onto the element the gesture is made over. Either callback may be null, which is
 * how a route with nothing to ask about takes no gesture: a swipe that opened an empty
 * pane would be worse than one that does nothing.
 */
export function useEdgeSwipe(on: {
  left?: (() => void) | null;
  right?: (() => void) | null;
}): EdgeSwipeHandlers {
  const start = useRef<{ id: number; x: number; y: number } | null>(null);

  // Plain closures rather than `useCallback`: they are handed to one DOM element, which
  // re-attaches them for nothing, and memoising them would only pin a stale `on`.
  return {
    onPointerDown(event) {
      start.current = null;
      if (event.pointerType !== "touch" || !event.isPrimary) return;
      if (!swipeAllowedFrom(event.target)) return;
      start.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
    },
    onPointerUp(event) {
      const from = start.current;
      start.current = null;
      if (!from || from.id !== event.pointerId) return;
      const direction = swipeDirection(event.clientX - from.x, event.clientY - from.y);
      if (direction === "left") on.left?.();
      if (direction === "right") on.right?.();
    },
    onPointerCancel() {
      start.current = null;
    },
  };
}
